import { useEffect, useRef, useState } from 'react';
import type { Candidate, Corrections, DimEvent, DimState, Frame } from '../fsm/machine';
import { deviceFingerprint } from '../capture/camera';
import { evaluateGates, type GateThresholds } from '../capture/gates';
import { guideFor, type ScenarioKey } from '../capture/guide';
import { DEFAULT_ORIENTATION_LIMITS } from '../capture/orientation';
import { useCaptureSession } from '../capture/useCaptureSession';
import { PitchGauge, ScenarioOutline, ScenarioToggle } from '../ui/ScenarioGuide';
import { Button, Card, Field, Hint, Screen, Spinner, ValueRow } from '../ui/Ui';

type Dispatch = (e: DimEvent) => void;

// ── Identify ─────────────────────────────────────────────────────────────────────────────────────────────────

/** L'imager della pistola è una tastiera: scrive nel campo a fuoco e manda Invio (regola del fuoco spaziale di Quick). */
export function IdentifyScreen({ s, dispatch }: { s: DimState; dispatch: Dispatch }) {
  const [id, setId] = useState('');
  const ref = useRef<HTMLInputElement>(null);
  useEffect(() => ref.current?.focus(), []);
  const submit = (): void => {
    const v = id.trim();
    if (v.length > 0) dispatch({ type: 'ITEM_SCANNED', itemId: v });
  };
  return (
    <Screen
      title="Dim — bancale"
      footer={
        <>
          <Button onClick={submit} disabled={id.trim().length === 0}>
            Avanti
          </Button>
          {s.session?.returnUrl && (
            <Button tone="secondary" onClick={() => dispatch({ type: 'ABANDON' })}>
              Torna a Quick
            </Button>
          )}
        </>
      }
    >
      <Card>
        <p className="mb-2 text-[15px] text-sec">Spara il codice del pallet</p>
        <input
          ref={ref}
          className="min-h-tocco w-full rounded-lg bg-riemp px-3 font-mono text-[20px] outline-none focus:ring-2 focus:ring-blu"
          value={id}
          inputMode="text"
          autoCapitalize="characters"
          onChange={(e) => setId(e.target.value)}
          onKeyDown={(e) => e.key === 'Enter' && submit()}
        />
      </Card>
      <p className="text-center text-[13px] text-ter">
        {s.session?.login} · {s.session?.iss} {s.session?.codSoc}
      </p>
    </Screen>
  );
}

// ── Guide (fotocamera, gate, anello, scatto) ───────────────────────────────────────────────────────────────

export function GuideScreen({ s, thresholds, automatic, dispatch }: { s: DimState; thresholds: GateThresholds; automatic: boolean; dispatch: Dispatch }) {
  const videoRef = useRef<HTMLVideoElement>(null);
  const scratchRef = useRef<HTMLCanvasElement>(null);
  const [busy, setBusy] = useState(false);
  const autoFiredRef = useRef(false);
  // Quale guida mostrare: il server riconosce lo scenario dalle righe di bordo, qui serve solo a dire all'operatore come inquadrare.
  const [scenario, setScenario] = useState<ScenarioKey>('Postazione');
  // Stessa sessione di cattura del recorder (WP1): fotocamera, sensore, richiami e anello vivono in un posto solo.
  const cap = useCaptureSession(videoRef, scratchRef, {
    thresholds,
    limits: DEFAULT_ORIENTATION_LIMITS,
    onLive: (state) => {
      // Lo scatto automatico solo da fermi, con l'immagine buona e la pistola in una posizione accettabile.
      if (automatic && state.ring >= 1 && state.quality === null && state.orientationHint === null && !autoFiredRef.current) {
        autoFiredRef.current = true;
        void shoot();
      }
    },
  });
  const ring = cap.live?.ring ?? 0;
  const cameraError = cap.error;

  const shoot = async (): Promise<void> => {
    if (busy) return;
    setBusy(true);
    try {
      const shot = await cap.shoot();
      const rejected = evaluateGates(shot.gates, thresholds);
      if (rejected) {
        autoFiredRef.current = false;
        dispatch({ type: 'GATE_REJECTED', hint: rejected });
        return;
      }
      const frame: Frame = {
        blob: shot.blob,
        widthPx: shot.widthPx,
        heightPx: shot.heightPx,
        gates: shot.gates,
        gravity: shot.gravity,
        capturedAtUtc: shot.capturedAtUtc,
        settings: shot.settings,
      };
      dispatch({ type: 'FRAME_CAPTURED', frame });
    } catch (e) {
      dispatch({ type: 'FATAL', message: e instanceof Error ? e.message : String(e) });
    } finally {
      setBusy(false);
    }
  };

  const circumference = 2 * Math.PI * 26;
  const hint = s.hint ?? cap.live?.quality ?? cap.live?.orientationHint ?? null;
  // Il messaggio sopra l'immagine: errore della fotocamera, richiamo dei gate o di posizione, oppure lo stato dell'anello.
  const message: { tone: 'bad' | 'warn' | 'ok'; text: string } = cameraError
    ? { tone: 'bad', text: `Fotocamera non disponibile: ${cameraError}. Usa "A mano".` }
    : hint
      ? { tone: s.hint ? 'bad' : 'warn', text: hint.hint }
      : { tone: 'ok', text: ring >= 1 ? (automatic ? 'Fermo: scatto…' : 'Fermo: scatta') : 'Tieni ferma la pistola' };
  const messageCls = { bad: 'bg-rosso/90', warn: 'bg-arancio/90', ok: 'bg-verde/90' }[message.tone];
  return (
    // SCHERMATA DI SCATTO a tutto schermo, come una app fotocamera. Il video della pistola in verticale è 1200×1600 e lo schermo
    // 320×405: `object-contain` lo mostra INTERO (ciò che si vede è ciò che finisce nella foto). Il disegno della guida usa lo stesso
    // rapporto 3:4 con `meet`, così resta allineato al video (a una pistola tenuta in orizzontale non corrisponderebbe).
    <div className="fixed inset-0 bg-black">
      <video ref={videoRef} playsInline muted className="absolute inset-0 h-full w-full object-contain" />
      <canvas ref={scratchRef} className="hidden" />
      {/* Guida: il disegno dello scenario (dove deve stare il pallet), la livella dell'inclinazione, l'anello di stabilità. Le quote arrivano in revisione. */}
      <svg viewBox="0 0 300 400" preserveAspectRatio="xMidYMid meet" className="pointer-events-none absolute inset-0 h-full w-full">
        <ScenarioOutline scenario={scenario} />
        <PitchGauge orientation={cap.live?.orientation ?? null} limits={DEFAULT_ORIENTATION_LIMITS} />
        <circle cx="262" cy="52" r="26" fill="none" stroke="#FFFFFF" strokeOpacity="0.3" strokeWidth="5" />
        <circle
          className="anello"
          cx="262"
          cy="52"
          r="26"
          fill="none"
          stroke={ring >= 1 ? '#34C759' : '#FF9500'}
          strokeWidth="5"
          strokeDasharray={circumference}
          strokeDashoffset={circumference * (1 - ring)}
          transform="rotate(-90 262 52)"
        />
      </svg>

      {/* A destra c'è l'anello di stabilità (~84 px): il testo non ci va sotto. */}
      <div className="absolute inset-x-0 top-0 bg-gradient-to-b from-black/70 to-transparent pb-6 pl-3 pr-[84px] pt-2 text-white">
        <p className="text-[15px] font-semibold">{s.itemId ?? 'Inquadra'}</p>
        <p className="text-[12px] text-white/80">{guideFor(scenario).short}</p>
        <div className="mt-1">
          <ScenarioToggle value={scenario} onChange={setScenario} />
        </div>
        {s.itemWarning && <p className="mt-1 rounded-lg bg-arancio/90 px-2 py-1 text-[13px]">{s.itemWarning}</p>}
      </div>

      <div className="absolute inset-x-0 bottom-0 bg-gradient-to-t from-black/80 to-transparent px-3 pb-3 pt-8">
        <p className={`mb-3 rounded-lg px-3 py-2 text-[15px] text-white ${messageCls}`}>{message.text}</p>
        <div className="flex items-center justify-between gap-2">
          <button type="button" onClick={() => dispatch({ type: 'MANUAL' })} className="min-h-tocco min-w-[72px] rounded-xl bg-white/25 px-3 text-[15px] font-semibold text-white active:opacity-70">
            A mano
          </button>
          <button
            type="button"
            aria-label="Scatta"
            disabled={busy || cameraError !== null}
            onClick={() => void shoot()}
            className={`h-16 w-16 rounded-full bg-white ring-4 active:scale-95 disabled:opacity-40 ${ring >= 1 ? 'ring-verde' : 'ring-white/40'}`}
          />
          <button type="button" onClick={() => dispatch({ type: 'ABANDON' })} className="min-h-tocco min-w-[72px] rounded-xl bg-rosso/80 px-3 text-[15px] font-semibold text-white active:opacity-70">
            Annulla
          </button>
        </div>
      </div>
    </div>
  );
}

// ── Review / Correct ─────────────────────────────────────────────────────────────────────────────────────────

function cm(v: number): string {
  return (v / 10).toFixed(0);
}

export function ReviewScreen({ s, dispatch }: { s: DimState; dispatch: Dispatch }) {
  const c = s.candidate!;
  const m = c.measurement;
  const [url, setUrl] = useState<string | null>(null);
  useEffect(() => {
    if (!s.frame) return;
    const u = URL.createObjectURL(s.frame.blob);
    setUrl(u);
    return () => URL.revokeObjectURL(u);
  }, [s.frame]);
  return (
    <Screen
      title="Controlla"
      footer={
        <>
          <Button onClick={() => dispatch({ type: 'CONFIRM' })}>Conferma</Button>
          <div className="flex gap-2">
            <Button tone="secondary" onClick={() => dispatch({ type: 'CORRECT' })}>
              Correggi
            </Button>
            <Button tone="secondary" onClick={() => dispatch({ type: 'RETAKE' })}>
              Rifai
            </Button>
            <Button tone="danger" onClick={() => dispatch({ type: 'ABANDON' })}>
              Annulla
            </Button>
          </div>
        </>
      }
    >
      {/* La foto della pistola in verticale è 1200×1600: intera (con `object-cover` in un riquadro 4:3 se ne perdeva il 44 %). */}
      {url && (
        <div className="flex justify-center">
          <img src={url} alt="" className="h-[232px] max-w-full rounded-xl object-contain" />
        </div>
      )}
      <Card>
        {/* Frame di cattura: frontMm è il lato che si vede dalla corsia, depthMm quello verso il fondo. */}
        <ValueRow label="Larghezza (fronte)" value={m.frontMm?.display ?? '—'} />
        <ValueRow label="Profondità" value={m.depthMm?.display ?? '—'} sub={m.depthAssumed ? 'dal tipo di bancale' : undefined} />
        <ValueRow label="Altezza da terra" value={m.heightFloorToTopMm?.display ?? '—'} />
        {m.volumeMm3 !== null && <ValueRow label="Volume" value={`${(m.volumeMm3 / 1e9).toFixed(2)} m³`} />}
        <ValueRow label="Bancale" value={m.palletTypeMeasured ?? '?'} sub={m.labelMatched ? 'etichetta letta' : 'etichetta non confermata'} />
      </Card>
      {m.warnings.map((w) => (
        <Hint key={w} tone="warn">
          {w}
        </Hint>
      ))}
      <p className="text-[12px] text-ter">
        {m.tier} · {m.accuracyClass} · {m.scenario} · {c.algorithmVersion}
      </p>
    </Screen>
  );
}

const REASONS = ['cima coperta dal film', 'sbordo non visto', 'bancale non standard', 'altro'];

export function CorrectScreen({ s, dispatch }: { s: DimState; dispatch: Dispatch }) {
  const m = s.candidate!.measurement;
  // La «larghezza» dell'operatore è il fronte (frontMm), la profondità è depthMm: stessi nomi di Measurement e del recorder.
  const [front, setFront] = useState(m.frontMm ? cm(m.frontMm.value) : '');
  const [depth, setDepth] = useState(m.depthMm ? cm(m.depthMm.value) : '');
  const [h, setH] = useState(m.heightFloorToTopMm ? cm(m.heightFloorToTopMm.value) : '');
  const [reason, setReason] = useState(REASONS[0]!);
  const save = (): void => {
    const corr: Corrections = { reason };
    const toMm = (v: string): number | undefined => (v.trim() === '' ? undefined : Math.round(Number(v.replace(',', '.')) * 10));
    const fm = toMm(front);
    const dm = toMm(depth);
    const hm = toMm(h);
    if (fm !== undefined && fm !== m.frontMm?.value) corr.frontMm = fm;
    if (dm !== undefined && dm !== m.depthMm?.value) corr.depthMm = dm;
    if (hm !== undefined && hm !== m.heightFloorToTopMm?.value) corr.heightFloorToTopMm = hm;
    dispatch({ type: 'CORRECTIONS', corrections: corr });
  };
  const valid = [front, depth, h].every((v) => v.trim() === '' || Number(v.replace(',', '.')) > 0);
  return (
    <Screen
      title="Correggi"
      footer={
        <>
          <Button onClick={save} disabled={!valid}>
            Salva corretto
          </Button>
          <Button tone="secondary" onClick={() => dispatch({ type: 'BACK' })}>
            Indietro
          </Button>
        </>
      }
    >
      <Card>
        <Field label="Larghezza" value={front} onChange={setFront} unit="cm" />
        <Field label="Profondità" value={depth} onChange={setDepth} unit="cm" />
        <Field label="Altezza da terra" value={h} onChange={setH} unit="cm" />
        <p className="mt-1 text-[13px] text-ter">Larghezza: il lato che vedi dalla corsia. Profondità: il lato verso il fondo.</p>
      </Card>
      <Card>
        <p className="mb-2 text-[15px] text-sec">Motivo</p>
        <div className="flex flex-wrap gap-2">
          {REASONS.map((r) => (
            <button key={r} type="button" onClick={() => setReason(r)} className={`min-h-tocco rounded-lg px-3 text-[15px] ${r === reason ? 'bg-blu text-white' : 'bg-riemp text-blu'}`}>
              {r}
            </button>
          ))}
        </div>
      </Card>
      <p className="text-[13px] text-ter">Un valore corretto a mano viene salvato con incertezza zero e con il motivo nello storico.</p>
    </Screen>
  );
}

// ── Manual (Tier C) ──────────────────────────────────────────────────────────────────────────────────────────

export function ManualScreen({ s, onSubmit, dispatch }: { s: DimState; onSubmit: (v: { frontMm: number; depthMm: number; heightFloorToTopMm: number; reason: string; deviceFingerprint: string }) => Promise<Candidate>; dispatch: Dispatch }) {
  // Larghezza = il fronte (frontMm), profondità = depthMm: stessi nomi di Measurement e del recorder.
  const [front, setFront] = useState('');
  const [depth, setDepth] = useState('');
  const [h, setH] = useState('');
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  const toMm = (v: string): number => Math.round(Number(v.replace(',', '.')) * 10);
  const valid = [front, depth, h].every((v) => Number(v.replace(',', '.')) > 0);
  const submit = async (): Promise<void> => {
    setBusy(true);
    setErr(null);
    try {
      const candidate = await onSubmit({ frontMm: toMm(front), depthMm: toMm(depth), heightFloorToTopMm: toMm(h), reason: s.hint?.hint ?? 'scelta dell’operatore', deviceFingerprint: (await deviceFingerprint()).fingerprint });
      dispatch({ type: 'MANUAL_CANDIDATE', candidate });
    } catch (e) {
      setErr(e instanceof Error ? e.message : String(e));
    } finally {
      setBusy(false);
    }
  };
  return (
    <Screen
      title="Misura a mano"
      footer={
        <>
          <Button onClick={() => void submit()} disabled={!valid || busy}>
            Salva
          </Button>
          <div className="flex gap-2">
            <Button tone="secondary" onClick={() => dispatch({ type: 'BACK' })}>
              Alla fotocamera
            </Button>
            <Button tone="danger" onClick={() => dispatch({ type: 'ABANDON' })}>
              Annulla
            </Button>
          </div>
        </>
      }
    >
      {s.hint && <Hint tone="info">{s.hint.hint}</Hint>}
      <Card>
        <Field label="Larghezza" value={front} onChange={setFront} unit="cm" autoFocus />
        <Field label="Profondità" value={depth} onChange={setDepth} unit="cm" />
        <Field label="Altezza da terra" value={h} onChange={setH} unit="cm" />
        <p className="mt-1 text-[13px] text-ter">Larghezza: il lato che vedi dalla corsia. Profondità: il lato verso il fondo.</p>
      </Card>
      {err && <Hint tone="bad">{err}</Hint>}
      <p className="text-[13px] text-ter">Metro a nastro, da terra alla cima del carico. Il valore viene salvato come manuale.</p>
    </Screen>
  );
}

// ── Stati semplici ───────────────────────────────────────────────────────────────────────────────────────────

export function WaitScreen({ title, label }: { title: string; label: string }) {
  return (
    <Screen title={title}>
      <Spinner label={label} />
    </Screen>
  );
}

export function NetworkLostScreen({ dispatch }: { dispatch: Dispatch }) {
  return (
    <Screen
      title="Rete assente"
      footer={
        <>
          <Button onClick={() => dispatch({ type: 'RETRY' })}>Riprova</Button>
          <Button tone="danger" onClick={() => dispatch({ type: 'ABANDON' })}>
            Annulla
          </Button>
        </>
      }
    >
      <Hint tone="warn">Il server non risponde. La misura non è persa: riprova quando il Wi-Fi torna (entro 30 minuti).</Hint>
    </Screen>
  );
}

export function ExpiredScreen({ dispatch }: { dispatch: Dispatch }) {
  return (
    <Screen
      title="Sessione scaduta"
      footer={
        <>
          <Button onClick={() => dispatch({ type: 'RETAKE' })}>Rifai la foto</Button>
          <Button tone="danger" onClick={() => dispatch({ type: 'ABANDON' })}>
            Annulla
          </Button>
        </>
      }
    >
      <Hint tone="bad">Sono passati più di 30 minuti dallo scatto: il server non ha più il fotogramma. Si rifà, non si perde niente di salvato.</Hint>
    </Screen>
  );
}

export function DoneScreen({ s, onReturn, dispatch }: { s: DimState; onReturn: (() => void) | null; dispatch: Dispatch }) {
  const r = s.result;
  return (
    <Screen
      title={s.abandoned ? 'Annullato' : 'Salvato'}
      footer={
        <>
          {onReturn && <Button onClick={onReturn}>Torna a Quick</Button>}
          <Button tone={onReturn ? 'secondary' : 'primary'} onClick={() => dispatch({ type: 'NEW_MEASUREMENT' })}>
            Nuova misura
          </Button>
        </>
      }
    >
      {s.abandoned ? (
        <Hint tone="info">Nessuna misura salvata.</Hint>
      ) : (
        <Hint tone="ok">
          Misura n. {r?.commitSeq} salvata{r?.replayed ? ' (era già salvata: nessun doppione)' : ''}.
        </Hint>
      )}
    </Screen>
  );
}

export function FatalScreen({ s, onReturn }: { s: DimState; onReturn: (() => void) | null }) {
  return (
    <Screen title="Dim" footer={onReturn ? <Button onClick={onReturn}>Torna a Quick</Button> : undefined}>
      <Hint tone="bad">{s.fatal ?? 'Errore'}</Hint>
      <p className="text-[13px] text-ter">Dim si apre da Quick: la voce del menu genera il codice di avvio.</p>
    </Screen>
  );
}
